import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CreditCard, CheckCircle2, AlertCircle, RefreshCw, X, Loader2, ExternalLink, Clock } from "lucide-react";

export default function BillingPage() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [paypalOpened, setPaypalOpened] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: s }, { data: c }] = await Promise.all([
        api.get("/billing/status"),
        api.get("/billing/config"),
      ]);
      setStatus(s);
      setConfig(c);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (params.get("returned") === "1") setInfo("Approvazione PayPal ricevuta. Verifico lo stato…");
  }, [params]);

  const onOpenHosted = () => {
    if (!config?.subscribe_url) return;
    window.open(config.subscribe_url, "_blank", "noopener,noreferrer");
    setPaypalOpened(true);
  };

  const onConfirmHostedPayment = async () => {
    setLoading(true);
    setError("");
    try {
      await api.post("/billing/notify-payment");
      await load();
      await refresh();
      setInfo("Pagamento segnalato. Un amministratore confermerà a breve.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const onSubscribeApi = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/billing/subscribe");
      if (data.approval_url) window.location.href = data.approval_url;
      else setError("Nessun link di approvazione restituito da PayPal.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  const onCancel = async () => {
    if (!window.confirm("Cancellare l'abbonamento?")) return;
    setLoading(true);
    try {
      await api.post("/billing/cancel");
      await load();
      await refresh();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!status || !config) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center font-mono-tel text-zinc-500 uppercase">
        Caricamento billing…
      </div>
    );
  }

  const isActive = status.subscription_status === "active";
  const isTrial = status.subscription_status === "trial";
  const isExpired = status.subscription_status === "expired";
  const isCancelled = status.subscription_status === "cancelled";
  const isPending = status.subscription_status === "pending_approval";
  const hosted = !!config.subscribe_url;
  const apiReady = config.configured && !hosted;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
      <div className="mb-8">
        <div className="font-mono-tel text-xs text-yellow-500 uppercase mb-2">[ Abbonamento ]</div>
        <h1 className="font-display font-black uppercase text-4xl md:text-5xl tracking-tight">
          Pit-Wall <span className="text-yellow-500">Pass</span>
        </h1>
        <p className="text-zinc-400 mt-2">Accesso illimitato alla console verdetti per la tua lega.</p>
      </div>

      <div className="card mb-6" data-testid="billing-status-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="section-h">Stato Attuale</div>
            <div className="font-display font-black uppercase text-2xl">
              {isActive && <span className="text-emerald-400">Attivo</span>}
              {isTrial && <span className="text-yellow-500">Trial — {status.free_uses_remaining} verdetto gratis</span>}
              {isExpired && <span className="text-red-400">Trial scaduto</span>}
              {isCancelled && <span className="text-orange-400">Cancellato</span>}
              {isPending && <span className="text-yellow-500">In attesa di conferma admin</span>}
              {status.is_admin && <span className="text-yellow-500"> · Admin</span>}
            </div>
            {status.subscription_started_at && (
              <div className="text-xs text-zinc-500 font-mono-tel mt-1">
                Attivo dal {new Date(status.subscription_started_at).toLocaleDateString()}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card border-yellow-500/40 mb-6">
        <div className="flex items-baseline justify-between flex-wrap gap-3">
          <div>
            <div className="section-h">Piano Mensile</div>
            <div className="font-display font-black uppercase text-4xl">
              €{config.price_eur}{" "}
              <span className="text-sm text-zinc-500 normal-case font-body font-normal">/ mese</span>
            </div>
            <ul className="text-sm text-zinc-300 mt-4 space-y-1.5">
              {[
                "Verdetti illimitati",
                "Riconoscimento piloti F1 25",
                "Doppio video / contro-POV",
                "Regole della Lega personalizzabili",
                "Export PDF con logo della tua lega",
                "Cancellazione in qualunque momento",
              ].map((t) => (
                <li key={t} className="flex gap-2">
                  <CheckCircle2 className="w-4 h-4 text-yellow-500 mt-0.5 flex-none" /> {t}
                </li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-2 min-w-[220px]">
            {!isActive && !isPending && hosted && (
              <>
                {!paypalOpened ? (
                  <button onClick={onOpenHosted} className="btn-primary" data-testid="open-paypal-button">
                    <ExternalLink className="w-4 h-4" /> Sottoscrivi su PayPal
                  </button>
                ) : (
                  <button
                    onClick={onConfirmHostedPayment}
                    disabled={loading}
                    className="btn-primary"
                    data-testid="confirm-payment-button"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    {loading ? "Notifica…" : "Ho completato il pagamento"}
                  </button>
                )}
                {paypalOpened && (
                  <button onClick={onOpenHosted} className="btn-secondary text-xs">
                    <ExternalLink className="w-3 h-3" /> Riapri PayPal
                  </button>
                )}
              </>
            )}
            {!isActive && !isPending && !hosted && apiReady && (
              <button onClick={onSubscribeApi} disabled={loading} className="btn-primary" data-testid="subscribe-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                {loading ? "Reindirizzo…" : "Sottoscrivi con PayPal"}
              </button>
            )}
            {isPending && (
              <div
                className="border border-yellow-500/40 bg-yellow-500/10 text-yellow-300 p-3 flex items-start gap-2"
                data-testid="pending-banner"
              >
                <Clock className="w-4 h-4 mt-0.5 flex-none" />
                <div className="text-sm">
                  Richiesta ricevuta. Riceverai l'attivazione non appena un admin avrà verificato il pagamento su PayPal.
                </div>
              </div>
            )}
            {isActive && (
              <button onClick={onCancel} disabled={loading} className="btn-danger" data-testid="cancel-button">
                <X className="w-4 h-4" /> Cancella abbonamento
              </button>
            )}
            {!isActive && !config.configured && !status.is_admin && (
              <div className="text-xs text-yellow-500 border border-yellow-500/40 bg-yellow-500/10 p-2 text-center font-mono-tel uppercase tracking-wider">
                PayPal in configurazione
              </div>
            )}
          </div>
        </div>

        {hosted && !isActive && !isPending && paypalOpened && (
          <div className="mt-5 border-t border-zinc-800 pt-4 text-sm text-zinc-400">
            <div className="font-display font-bold uppercase text-xs tracking-widest text-yellow-500 mb-2">
              Come funziona
            </div>
            <ol className="list-decimal list-inside space-y-1 text-zinc-400">
              <li>Si è aperto PayPal in una nuova finestra: completa il pagamento di €{config.price_eur}.</li>
              <li>Torna su questa pagina e premi <strong className="text-white">"Ho completato il pagamento"</strong>.</li>
              <li>Un amministratore confermerà entro poche ore e il tuo account diventerà <strong className="text-emerald-400">Attivo</strong>.</li>
            </ol>
          </div>
        )}
      </div>

      {info && (
        <div className="border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-sm p-3 mb-4 flex gap-2">
          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-none" /> {info}
        </div>
      )}
      {error && (
        <div className="border border-red-500/40 bg-red-500/10 text-red-400 text-sm p-3 mb-4 flex gap-2" data-testid="billing-error">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-none" /> {error}
        </div>
      )}

      <div className="mt-8 flex items-center gap-2 flex-wrap">
        <button onClick={() => navigate("/dashboard")} className="btn-secondary" data-testid="back-to-dashboard">
          Vai alla console
        </button>
        {status.is_admin && (
          <button onClick={() => navigate("/admin")} className="btn-secondary">
            Pannello Admin
          </button>
        )}
        <button onClick={load} className="btn-secondary" data-testid="reload-billing">
          <RefreshCw className="w-4 h-4" /> Aggiorna
        </button>
      </div>
    </div>
  );
}
